/**
 * Payer-related data fetchers
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { useUser } from "@/src/context/user-context";
import { toast } from "@/src/hooks/use-toast";
import {
  getInsuranceMappingPayerIds,
  payerMatchesInsuranceMappingAllowlist,
} from "@/src/lib/insurance-mapping-payer-ids";
import { logger } from "@/src/lib/logging/logger";
import { Payer } from "@/src/models/payer";
import { createOrUpdatePayer, updatePayer } from "@/src/repo/payer";
import { getAllPayers, getPayerByShortId } from "@/src/requests/payer";
import { isNotFound } from "@/src/requests/utils/fetch-json";

export const PAYERS_CACHE_KEY = "payers";

export function usePayers() {
  const query = useQuery({
    queryKey: [PAYERS_CACHE_KEY],
    queryFn: getAllPayers,
    retry: false,
    staleTime: Infinity,
  });

  return {
    payers: query.data || [],
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useAvailablePayers(currentCustomerPayer?: string | null) {
  const { payers, isFetching, isLoading, isError, error, refetch } = usePayers();
  const { group } = useUser();

  const filteredPayers = useMemo(() => {
    if (!group?.subdomain) return payers;

    const allowedPayerIds = getInsuranceMappingPayerIds(group);

    if (!allowedPayerIds) return payers;

    return payers.filter((payer) =>
      payerMatchesInsuranceMappingAllowlist(payer, allowedPayerIds, currentCustomerPayer)
    );
  }, [payers, group?.subdomain, currentCustomerPayer]);

  return { payers: filteredPayers, isFetching, isLoading, isError, error, refetch };
}

export function usePayerByShortId(shortId: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const {
    data: payer,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [PAYERS_CACHE_KEY, shortId],
    queryFn: async () => {
      const payersCache = queryClient.getQueryData<Payer[]>([PAYERS_CACHE_KEY]);

      if (payersCache && payersCache.find((payer) => payer.shortId === shortId)) {
        logger.info("Found payer in cache");

        return payersCache.find((payer) => payer.shortId === shortId);
      }

      logger.info("Fetching payer from server");

      try {
        const payer = await getPayerByShortId(shortId);

        if (payer) {
          updatePayersQueryData(queryClient, payer);
        }

        return payer;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        throw err;
      }
    },
    staleTime: Infinity,
    enabled: options?.enabled !== false && !!shortId,
  });

  return { payer, isFetching, isLoading, isError };
}

export const updatePayersQueryData = (queryClient: QueryClient, updatedPayer: Payer) => {
  queryClient.setQueryData([PAYERS_CACHE_KEY], (oldData: Payer[] = []) => {
    const current = oldData ?? [];
    const exists = current.some((payer) => payer.id === updatedPayer.id);

    if (exists) {
      return current.map((payer) => (payer.id === updatedPayer.id ? updatedPayer : payer));
    }

    return [updatedPayer, ...current];
  });

  if (updatedPayer.shortId) {
    queryClient.setQueryData([PAYERS_CACHE_KEY, updatedPayer.shortId], updatedPayer);
  }

  if (updatedPayer.id) {
    queryClient.setQueryData([PAYERS_CACHE_KEY, updatedPayer.id], updatedPayer);
  }
};

export function usePayerUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payer: Payer) => {
      const result = await updatePayer(payer.id, payer);

      if (!result) {
        throw new Error("Failed to update payer");
      }

      return result;
    },
    onMutate: async (payer) => {
      await queryClient.cancelQueries({ queryKey: [PAYERS_CACHE_KEY] });

      const previousPayers = queryClient.getQueryData<Payer[]>([PAYERS_CACHE_KEY]) ?? [];

      if (previousPayers.find((p) => p.id === payer.id)) {
        updatePayersQueryData(queryClient, payer);
      }

      return { previousPayers };
    },
    onError: (error, variables, context) => {
      logger.error("Error updating payer");

      if (context) {
        queryClient.setQueryData([PAYERS_CACHE_KEY], context.previousPayers);
      }

      toast({
        title: "Failed to save payer",
        description: "There was an error saving the payer. Please try again.",
        variant: "destructive",
      });
    },
    meta: {},
  });
}

export function usePayerCreateOrUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payer: Payer) => {
      const result = await createOrUpdatePayer(payer);

      if (!result) {
        throw new Error("Failed to create or update payer");
      }

      return result;
    },
    onMutate: async (payer) => {
      await queryClient.cancelQueries({ queryKey: [PAYERS_CACHE_KEY] });

      const previousPayers = queryClient.getQueryData<Payer[]>([PAYERS_CACHE_KEY]) ?? [];

      // Optimistically upsert the payer
      updatePayersQueryData(queryClient, payer);

      return { previousPayers };
    },
    onSuccess: (savedPayer) => {
      // Reconcile cache with saved result
      updatePayersQueryData(queryClient, savedPayer);
    },
    onError: (error, _variables, context) => {
      logger.error("Error creating payer");

      if (context) {
        queryClient.setQueryData([PAYERS_CACHE_KEY], context.previousPayers);
      }

      toast({
        title: "Failed to create payer",
        description: "There was an error creating the payer. Please try again.",
        variant: "destructive",
      });
    },
    meta: {},
  });
}
